import React, { Component } from 'react';
import { Link } from 'react-router';
import partial from 'lodash/partial';
import classnames from 'classnames';
import FaqList from 'common/components/faqs';
import { Meta } from 'common/components/meta';
import { getCanonicalURL } from 'common/utils';
import WhyCS from 'marketing/common/whyCS';
import Support from 'marketing/containers/support';
import IntercomChat from 'common/containers/intercom_chat';
import ReviewsAgg from 'marketing/containers/reviews_agg';
import { fetchReviewStatsIfNeeded } from 'marketing/ducks';
import {
  trackMixpanel, trackClickWithMixpanel, MP_VIEWED_MARKETING_PAGE_EVENT_NAME,
  MP_CLICKED_GET_STARTED_EVENT_NAME, MP_CLICKED_DESIGN_CONSULTATION_EVENT_NAME,
  MP_CLICKED_WRAPUP_FAQ_ITEM_EVENT_NAME, MP_CLICKED_CROWD_FAQ_ITEM_EVENT_NAME,
  MP_CLICKED_BRIEF_FAQ_ITEM_EVENT_NAME, MP_CLICKED_1_TO_1_FAQ_ITEM_EVENT_NAME
} from 'common/tracking';

const BRIEF_QUESTIONS = [{
  question: 'What types of projects can you post?',
  answer: "<p>Our designers can create beautiful and functional logos, websites, marketing materials, product packaging, product designs - pretty much any design for print and/or online use. Our namers can help you name a business or product. Take a look at our <a target='_blank' href='/categories/'>categories</a> and if you don't see what you are looking for, <a target='_blank' href='/help/contact/'>ask us</a>. We are friendly and happy to help.</p>",
}, {
  question: 'How long will it take?',
  answer: "<p>Projects run for 7 days, but you can shorten them to as little as 1 day if you're in a hurry. Many entries come in the last few days of a project, and we encourage you to wait until the project ends before picking your favorite (tell us if you want to close the project early). You can extend your project for free, up to 7 more days if you need more time.</p>",
}, {
  question: "How much will it cost and what's included?",
  answer: "<p>Crowd packages start at $299 in most categories, while projects with a single designer (1-to-1 projects) start at $200. All packages include the award to the designer and crowdspring's fee, a custom legal contract giving you full rights to the work you're buying, free focus groups (design projects only), our award-winning customer support, tools and platform. You can select packages and features based on what you need and your budget. You will always know upfront exactly what you'll pay, with no hidden costs and no surprises.</p>",
}, {
  question: "Will you own the work when you're done?",
  answer: "<p>Absolutely! Every project on crowdspring is protected by a custom legal contract giving you full rights to the work you're buying. If you have multiple awards, you'll have separate contracts for each. Once the contract is signed and the creative is paid after you approve the final deliverables in your project, you can use the design (or name) any way you like. You can change it or alter it in any way – it's yours.</p><p>The non-winning work remains the property of the person who created that work. This means you may not use those other entries (or any portions of them) in any way. But we give you an easy way to add awards and make offers in your projects when you find more than one design (or name) you love.</p>",
}];

const CROWD_QUESTIONS = [{
  question: 'How many entries can you expect?',
  answer: "<p>You'll see dozens of entries from multiple designers. We'll let you know every time a new entry or comment comes in and invite you to give feedback. The more feedback you provide, the more entries you'll receive. Years of experience show us that projects with active clients will receive more than double the entries compared to projects with low client participation.</p>",
}, {
  question: 'Can you ask designers to make changes?',
  answer: "<p>Absolutely! In fact, this is a key factor for success. Once the entries start rolling in, let the designers know what you think about their entries. You can score and give individual feedback. You can also update your brief if you want to address all participants in your project. They are happy to make changes and iterate on their designs.</p><p>When you score each entry (1 to 5 stars), your score and comments are private. Only you and the individual designer will see the star rating and comments on their design. Updates to your brief are public for all participants in the project.</p>",
}, {
  question: 'Can you buy more than one design or name?',
  answer: "<p>Like more than one entry? It happens all the time. You can start your project with multiple awards or add awards at any time during your project. You can also make offers on additional entries after your project has been awarded.</p>",
}, {
  question: 'Who sees what?',
  answer: "<p>Want to keep things on the down low? We offer privacy settings like search engine exclusion, non-disclosure agreements (NDA's), and private galleries so only you can see the designs.</p><p>Want to know how your project compares to other projects? Look for the happy/sad face in your project. Click on it and we'll share stats about your participation and how your project compares to others. Creatives use these stats to decide whether to participate, so stay active and involved.</p>",
}];

const ONE_TO_ONE_QUESTIONS = [{
  question: 'How should you pick a designer?',
  answer: "<p>When you post a 1-to-1 project, we'll recommend our best designers who specialize in your project category, or you can work directly with any designer on crowdspring.</p>",
}, {
  question: 'What kinds of projects are best for 1-to-1?',
  answer: "<p>If you know exactly what you need done, 1-to-1 projects work great. For example, you have a specific idea for your new logo and can provide art direction. But if you have only a general idea and want to see lots of variety, a crowd project would be best.</p>",
}, {
  question: 'Can you ask the designer to make changes?',
  answer: "<p>Absolutely! You'll iterate through various design concepts until you find something you love. You can score and comment on each design and the designer will update their work and submit revisions.</p>",
}, {
  question: 'How much do designers charge?',
  answer: "<p>You can negotiate the price for your 1-to-1 project (minimum is $200, including fees). You can set your budget and invite a designer to accept, or ask a designer to quote a price based on the scope of work you need done. When you post a 1-to-1 project, we'll always recommend our best designers who specialize in your project category, or you can work directly with any designer on crowdspring.</p>",
}];

const WRAPUP_QUESTIONS = [{
  question: 'How long will you have to pick the winner?',
  answer: "<p>You have seven days to select your favorite after the project closes. Sometimes, you might need an extra few days. Just let us know when that happens.</p><p>Before making the final decision, you may want to run a free focus group. All silver packages in design projects include one free focus group ($29 upgrade to unlimited). All other design packages include unlimited focus groups.</p><p>A focus group (also called a \"poll\") allows you to invite friends, customers, colleagues and anyone else to vote and comment on their favorite designs (you can assign weighted votes or let each vote count equally). Anyone is eligible to vote as long as they have the special voting URL we'll provide to you (they will not need to register an account). Your focus group can be public or private - you choose! You fully control whom you invite to each focus group and what designs are included in each focus group.</p>",
}, {
  question: 'What happens after you pick the winner?',
  answer: "<p>In design projects, you'll have a private area to complete the project with the designer you chose. If you have more than one award, you'll have a separate wrap-up with each designer. Each of you will sign the legal contract for the project (giving you full rights once the designer is paid).</p><p>You can continue to iterate and request changes in wrap-up to make sure the design is just right. Once you approve the proof files, the designer will create and send final files for your approval. Once you approve the final files, we pay the designer and you own full rights to the work.</p><p>In naming projects, you'll sign the contract and confirm the name and that's it - it's yours.</p>",
}, {
  question: 'What files will you need?',
  answer: "<p>For each project category, we'll recommend required file types and will ask the winning designer to provide you with appropriate final files. You can always request specific formats you or your printer require.<p>",
}, {
  question: 'Can you keep working with your designer?',
  answer: "<p>Yes! If you would like to continue working with the designer, you can negotiate an amount for the work and increase your award at any time during your wrap-up. You can also work with them directly in a <a target='_blank' href='/1to1-projects/'>1-to-1 project</a>.</p>",
}];

export default class How extends Component {
  constructor(props) {
    super(props);

    this.state = { heroCollapsed: false };
    this.handleScroll = this.handleScroll.bind(this);
  }

  componentDidMount() {
    trackMixpanel(MP_VIEWED_MARKETING_PAGE_EVENT_NAME, { pageName: "How it works" });

    window.addEventListener('scroll', this.handleScroll, true);
  }

  componentWillUnmount() {
    window.removeEventListener('scroll', this.handleScroll, true);
  }

  handleScroll(e) {
    const scrollerEl = e.target;
    const heroEl = document.getElementsByClassName('block-hero')[0];
    const stickyEl = document.getElementsByClassName('sticky')[0];
    const { heroCollapsed } = this.state;

    /* 16px before `stickyEl` */
    const referenceHeight = heroEl.offsetTop + heroEl.offsetHeight - stickyEl.offsetHeight - 16;

    if (heroCollapsed && scrollerEl.scrollTop <= referenceHeight) {
      this.setState({ heroCollapsed: false });
    } else if (!heroCollapsed && scrollerEl.scrollTop > referenceHeight) {
      this.setState({ heroCollapsed: true });
    }
  }

  render() {
    const { heroCollapsed } = this.state;

    return (
      <div id="content" className="l-content">
        <Meta
          title="How it Works"
          meta={[
            { name: 'robots', content: 'index,follow' },
            { name: 'description', content: 'Find out how easy it is to work with talented designers from around the world to get a professional design or business name you love. 100% money-back guarantee!' },
            { property: 'og:title', content: 'How crowdspring works' },
            { property: 'og:url', content: getCanonicalURL('/how-it-works/') },
          ]}
          link={[{ rel: 'canonical', href: getCanonicalURL('/how-it-works/') }]} />
        <section className={classnames("block block-hero", { collapsed: heroCollapsed })}>
          <div className="row">
            <div className="small-16 medium-16 large-10 columns">
              <h1 className="hero-title">How it Works</h1>
              <h3 className="hero-subtitle">
                We've dramatically simplified the design
                process. <br className="break-for-medium-up"/> It's never been easier to get a
                design that will <br className="break-for-medium-up"/> wow your customers and
                set you apart from your <br className="break-for-medium-up"/> competitors.
                Just follow these three simple steps:
              </h3>
            </div>
          </div>
          <div className="sticky">
            <div className="row">
              <div className="small-16 medium-16 large-12 columns">
                <div className="step">1. Brief <i className='fa fa-long-arrow-right'></i></div>
                <div className="step">2. Collaborate <i className='fa fa-long-arrow-right'></i></div>
                <div className="step">3. Wrap-up</div>
              </div>
              <div className="small-16 medium-16 large-4 columns">
                <Link className="btn btn-primary btn-lg btn-block" to="/start-a-project/">
                  <span onClick={
                    partial(trackClickWithMixpanel, MP_CLICKED_GET_STARTED_EVENT_NAME, {
                      pageName: "How it works", pageArea: "Hero"
                    })
                  }>
                    Get Started
                  </span>
                </Link>
              </div>
            </div>
          </div>
        </section>
        <section className="block how-step step-1">
          <div className="row">
            <div className="columns small-16 large-7 large-offset-1">
              <h2 className="step-title">
                <span className="number">1.</span>Tell us what you need.
              </h2>
              <p>
                To start your project, select one of our packages.<br/>
                We'll ask you a few simple questions to create your brief.<br/>
                The brief helps us understand your tastes and provides the designers
                the information they need to create something you'll love.
              </p>
              <ul className="step-features">
                <li><i className="fa fa-long-arrow-right"/>Interactive Q&A customized for your project category.</li>
                <li><i className="fa fa-long-arrow-right"/>Flexible pricing for everyone.</li>
                <li><i className="fa fa-long-arrow-right"/>Simple (takes just a few minutes) and proven process.</li>
              </ul>
              <div className="testimonial">
                <div className="quote">
                  "I loved working with crowdspring! It was so exciting to watch your project pick up steam as you gave feedback to the creatives. It felt very collaborative and you have so many creative people submitting ideas just for your project. Their system is organized, easy to use and understand. I am so happy with the results!"
                </div>
                <div className="author">
                  <img src="https://s3.amazonaws.com/crowdspring3-assets/marketing/crowdspring_marketing_testimonial_ali-hissey.jpg" /> Ali Hissey, Founder, Hissey Fit
                </div>
              </div>
              <div className="faqs">
                <h3>FAQs</h3>
                <FaqList data={BRIEF_QUESTIONS} showMore={true} eventTracking={{eventName: MP_CLICKED_BRIEF_FAQ_ITEM_EVENT_NAME, eventData: {pageName: "How it works", pageArea: "Brief FAQs"}}} />
              </div>
            </div>
            <div className="columns small-16 large-7">
              <div className="artwork-container">
                <img alt="Creative Brief" src="https://s3.amazonaws.com/crowdspring3-assets/marketing/crowdspring_marketing_how-it-works_step1%401x.png" className="hide-for-small-only" />
                <img alt="Creative Brief" src="https://s3.amazonaws.com/crowdspring3-assets/marketing/crowdspring_marketing_how-it-works_step1_mobile.png" className="show-for-small-only" />
              </div>
            </div>
          </div>
        </section>
        <section className="block how-step step-2">
          <div className="row">
            <div className="columns small-16 text-center">
              <h2 className="step-title">
                <span className="number">2.</span>
                Work with our über-talented designers
              </h2>
              <h3 className="subtitle">
                With over 220,000 designers around the globe<br className="break-for-medium-up"/>
                (from Australia to Zimbabwe, and everywhere in
                between),<br className="break-for-medium-up"/>our global design community is ready
                to help you 24/7.<br className="break-for-medium-up"/>Work with them in a way that
                works for you.
              </h3>
            </div>
          </div>
          <div className="row">
            <div className="columns small-16 large-7 large-offset-1">
              <h2>Work with the crowd</h2>
              <ul className="step-features">
                <li><i className="fa fa-long-arrow-right"/>Multiple designers submit dozens of concepts.</li>
                <li><i className="fa fa-long-arrow-right"/>Review, rate, and give feedback.</li>
                <li><i className="fa fa-long-arrow-right"/>One price. Pick the design you love.</li>
              </ul>
              <p>
                With a crowd project, designers share dozens of concepts based on your brief.
                Review and rate the designs as they arrive, and designers will revise as you
                provide feedback. When the project ends, just pick the one you love!
              </p>
              <div className="testimonial">
                <div className="quote">
                  "In 10 years I have launched 17 projects on crowdspring and have always been impressed. Crowdspring brings 10 maybe even 100 times more creativity than simply working with a single random creative I could have found outside of crowdspring where I might only see a couple different concepts. And the price is very affordable."
                </div>
                <div className="author">
                  <img src="https://s3.amazonaws.com/crowdspring3-assets/marketing/crowdspring-case-study-regine-steven-photo.png" /> Steven Krane, Entrepreneur & Investor
                </div>
              </div>
              <div className="faqs">
                <h3>Crowd FAQs</h3>
                <FaqList data={CROWD_QUESTIONS} showMore={true} eventTracking={{eventName: MP_CLICKED_CROWD_FAQ_ITEM_EVENT_NAME, eventData: {pageName: "How it works", pageArea: "Crowd FAQs"}}} />
              </div>
            </div>
            <div className="columns small-16 large-7">
              <div className="artwork-container">
                <img alt="Collaborate" src="https://s3.amazonaws.com/crowdspring3-assets/marketing/crowdspring_marketing_how-it-works_step2%401x.png" className="hide-for-small-only" />
                <img alt="Collaborate" src="https://s3.amazonaws.com/crowdspring3-assets/marketing/crowdspring_marketing_how-it-works_step2_mobile.png" className="show-for-small-only" />
              </div>
            </div>
          </div>
          <div className="row one-to-one-row hide-for-small-only">
            <div className="columns small-16 large-7 large-offset-1">
              <div className="artwork-container">
                <img alt="Work 1-to-1" src="https://s3.amazonaws.com/crowdspring3-assets/marketing/crowdspring_marketing_how-it-works_step2-1to1-no-background%402x.png" />
              </div>
            </div>
            <div className="columns small-16 large-7">
              <h2>Work 1-to-1</h2>
              <ul className="step-features">
                <li>
                  <i className="fa fa-long-arrow-right"/>Find a designer (or we can recommend one of our best).</li>
                <li><i className="fa fa-long-arrow-right"/>Negotiate a price that's fair.</li>
                <li>
                  <i className="fa fa-long-arrow-right"/>Use crowdspring tools to manage your project.</li>
              </ul>
              <p>
                Find a designer you love and negotiate a price to work directly with them. Leverage
                the power of the crowdspring platform to manage your project. You'll work together
                to tweak, tinker and tighten until you love it!
              </p>
              <div className="testimonial">
                <div className="quote">
                  "I needed to reimagine my logos and design as I was expanding the areas of my professional service business. I did a one-on-one project with the designer Arsorum who were simply fantastic - they took my complex brief and reimagined the design and approach very creatively. When I had questions the crowdspring team were on hand and answered very fast and with great help. A fantastic end to end experience."
                </div>
                <div className="author">
                  <img src="https://s3.amazonaws.com/crowdspring3-assets/marketing/crowdspring-marketing-how-it-works-paul-whybrow-photo.jpg" /> Paul Whybrow, PW Creative
                </div>
              </div>
              <div className="faqs">
                <h3>1-to-1 FAQs</h3>
                <FaqList data={ONE_TO_ONE_QUESTIONS} showMore={true} eventTracking={{eventName: MP_CLICKED_1_TO_1_FAQ_ITEM_EVENT_NAME, eventData: {pageName: "How it works", pageArea: "1-to-1 FAQs"}}} />
              </div>
            </div>
          </div>
          <div className="row columns text-center consultation-row">
            <h3>Not sure which is right for you?</h3>
            <Link to="/help/contact/consultation/" className="btn btn-text btn-lg">
              <span onClick={
                partial(trackClickWithMixpanel, MP_CLICKED_DESIGN_CONSULTATION_EVENT_NAME, {
                  pageName: "How it works", pageArea: "Step 2"
                })
              }>
                Get a free design consultation <i className="fa fa-long-arrow-right" />
              </span>
            </Link>
          </div>
        </section>
        <section className="block how-step step-3">
          <div className="row">
            <div className="columns small-16 large-15 large-offset-1">
              <h2 className="step-title">
                <span className="number">3.</span>
                Pick the winning design and finalize the project.
              </h2>
            </div>
          </div>
          <div className="row">
            <div className="columns small-16 large-7 large-offset-1">
              <p>
                When you're happy with a design, select it as the winner.<br/>
                You'll review design proofs and make refinements.<br/>
                After you approve the final files, crowdspring will pay the designer and
                you'll receive full intellectual property rights to the design.
              </p>
              <ul className="step-features">
                <li><i className="fa fa-long-arrow-right"/>Select the winner.</li>
                <li><i className="fa fa-long-arrow-right"/>Make final adjustments.</li>
                <li>
                  <i className="fa fa-long-arrow-right"/>Receive full rights to the work you purchased.</li>
              </ul>
              <div className="testimonial">
                <div className="quote">
                  "I just finished my 2nd of 3 projects with crowdspring. The first was an incredible book cover. This new project was artwork for a wooded token. I have a 3rd project on going for the development of a logo for graphic for my wife's jewelry business. I'll summarize - Don't waste your time at other sites - just come to crowdspring.com. The experience, the talent and the result will only make your life easier."
                </div>
                <div className="author">
                  <img src="https://s3.amazonaws.com/crowdspring3-assets/marketing/crowdspring-marketing-how-it-works-robert-dixon-photo.jpeg" /> Robert Dixon, Bob Dixon Consulting Services
                </div>
              </div>
              <div className="faqs">
                <h3>FAQs</h3>
                <FaqList data={WRAPUP_QUESTIONS} showMore={true} eventTracking={{eventName: MP_CLICKED_WRAPUP_FAQ_ITEM_EVENT_NAME, eventData: {pageName: "How it works", pageArea: "Wrapup FAQs"}}} />
              </div>
            </div>
            <div className="columns small-16 large-7">
              <div className="artwork-container">
                <img alt="Wrap-Up" src="https://s3.amazonaws.com/crowdspring3-assets/marketing/crowdspring_marketing_how-it-works_step3%401x.png" className="hide-for-small-only"/>
                <img alt="Wrap-Up" src="https://s3.amazonaws.com/crowdspring3-assets/marketing/crowdspring_marketing_how-it-works_step3_mobile.png" className="show-for-small-only"/>
              </div>
            </div>
          </div>
        </section>

        <section className="block">
          <div className="row text-center">
            <div className="columns small-16">
              <ReviewsAgg />
            </div>
          </div>
        </section>

        <WhyCS />

        <Support consultationEventTracking={{
          eventName: MP_CLICKED_DESIGN_CONSULTATION_EVENT_NAME,
          eventData: {
            pageName: "How it works",
            pageArea: "Support footer",
          }
        }} />

        <IntercomChat />
      </div>
    );
  }
};

How.need = [fetchReviewStatsIfNeeded];



// WEBPACK FOOTER //
// ./marketing/how.jsx